import { useMemo, useState, type ReactNode } from 'react'
import { LogStats } from '../useLogStats'
import { Shield, Server, Gauge, Users, Search } from 'lucide-react'

interface ProxyRow {
  proxy: string
  count: number
  cycles_sum: number
  types: Record<string, number>
  difficulties: Record<string, number>
}

function fmtAvg(sum: number, n: number, digits = 1): string {
  if (!n) return '—'
  const v = sum / n
  return Number.isInteger(v) ? String(v) : v.toFixed(digits)
}

function fmtPct(part: number, whole: number): string {
  if (!whole) return '—'
  return `${Math.round((part / whole) * 100)}%`
}

function sortedEntries(obj: Record<string, number> | undefined): { name: string; count: number }[] {
  if (!obj) return []
  return Object.entries(obj)
    .map(([name, count]) => ({ name, count: Number(count) || 0 }))
    .filter(r => r.count > 0)
    .sort((a, b) => b.count - a.count)
}

function BarRow({ name, count, max, tone }: { name: string; count: number; max: number; tone: 'violet' | 'amber' | 'sky' }) {
  const pct = max > 0 ? Math.max(2, Math.round((count / max) * 100)) : 0
  const fill =
    tone === 'violet' ? 'bg-violet-500/80' :
    tone === 'amber' ? 'bg-amber-500/80' : 'bg-sky-500/80'
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_3.5rem] gap-2 items-center py-1">
      <div className="min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-xs text-foreground truncate font-medium">{name}</span>
        </div>
        <div className="h-1.5 rounded-full bg-muted/60 overflow-hidden">
          <div className={`h-full rounded-full ${fill}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
      <span className="text-xs tabular-nums font-semibold text-right text-muted-foreground">{count.toLocaleString('ru-RU')}</span>
    </div>
  )
}

function TypeChips({ types }: { types: Record<string, number> }) {
  const rows = sortedEntries(types).slice(0, 4)
  if (!rows.length) return <span className="text-muted-foreground">—</span>
  return (
    <div className="flex flex-wrap gap-1">
      {rows.map(r => (
        <span
          key={r.name}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] bg-violet-500/10 text-violet-300 border border-violet-500/20"
          title={`${r.name}: ${r.count}`}
        >
          <span className="max-w-[9rem] truncate">{r.name}</span>
          <span className="tabular-nums font-semibold opacity-80">{r.count}</span>
        </span>
      ))}
    </div>
  )
}

/** Profiler-only captcha analytics — proxy / type / difficulty / cycles. */
export default function CaptchaDetailPanel({ stats }: { stats: LogStats }) {
  const [q, setQ] = useState('')
  const d = stats.profilerCaptchaDetail

  const solved = Number(d?.solved) || 0
  const cyclesSum = Number(d?.cycles_sum) || 0
  const profilesOk = Number(d?.profiles_ok) || Number(stats.profilerOkCount) || 0
  const profileCapSum = Number(d?.profile_cap_sum) || 0
  const profilesWithCap = Number(d?.profiles_with_cap) || 0

  const byProxyAll: ProxyRow[] = useMemo(() => Object.entries(d?.by_proxy || {})
    .map(([proxy, row]: [string, any]) => ({
      proxy,
      count: Number(row?.count) || 0,
      cycles_sum: Number(row?.cycles_sum) || 0,
      types: (row?.types && typeof row.types === 'object') ? row.types : {},
      difficulties: (row?.difficulties && typeof row.difficulties === 'object') ? row.difficulties : {},
    }))
    .sort((a, b) => b.count - a.count), [d?.by_proxy])

  const byProxy = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return byProxyAll
    return byProxyAll.filter(r => r.proxy.toLowerCase().includes(needle))
  }, [byProxyAll, q])

  const byType = sortedEntries(d?.by_type)
  const byDiff = sortedEntries(d?.by_difficulty)
  const maxType = byType[0]?.count || 0
  const maxDiff = byDiff[0]?.count || 0
  const maxProxy = byProxy[0]?.count || 0

  const hotProxyShare = maxProxy > 0 && solved > 0 ? Math.round((maxProxy / solved) * 100) : 0

  return (
    <div className="h-full overflow-y-auto px-3 py-3 sm:px-4 select-text space-y-4">
      {/* Hero */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border/60 flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-violet-500/15 text-violet-400 shrink-0">
            <Shield className="h-4.5 w-4.5" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground">Капча и прокси</div>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
              Какие прокси чаще ловят капчу, какой тип и сложность, сколько циклов решений
              уходит на профиль. Данные с потока — в лог не пишутся.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-border/50">
          <HeroMetric
            icon={<Shield className="h-3.5 w-3.5" />}
            label="Решено капч"
            value={solved ? solved.toLocaleString('ru-RU') : '—'}
          />
          <HeroMetric
            icon={<Gauge className="h-3.5 w-3.5" />}
            label="Ср. циклов на капчу"
            value={fmtAvg(cyclesSum, solved)}
            hint="Сколько раундов решения в среднем на одну пройденную капчу"
          />
          <HeroMetric
            icon={<Users className="h-3.5 w-3.5" />}
            label="Ср. циклов на профиль"
            value={fmtAvg(profileCapSum, profilesOk)}
            hint="Среднее по успешным итерациям (включая профили без капчи = 0)"
          />
          <HeroMetric
            icon={<Server className="h-3.5 w-3.5" />}
            label="Профили с капчей"
            value={profilesWithCap
              ? `${profilesWithCap.toLocaleString('ru-RU')} · ${fmtPct(profilesWithCap, profilesOk)}`
              : '—'}
            hint="Доля успешных прогревов, где был хотя бы один цикл решения"
          />
        </div>
      </div>

      {!solved && (
        <div className="rounded-xl border border-dashed border-border bg-muted/20 px-4 py-8 text-center">
          <Shield className="h-8 w-8 mx-auto text-muted-foreground/40 mb-3" />
          <div className="text-sm font-medium text-foreground">Пока пусто</div>
          <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto leading-relaxed">
            После следующей капчи здесь появятся прокси, типы и средние циклы.
            В обычном логе эта телеметрия не показывается.
          </p>
        </div>
      )}

      {solved > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <section className="rounded-xl border border-border bg-card p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Типы капчи
            </h3>
            {byType.length === 0 ? (
              <p className="text-xs text-muted-foreground">Нет данных</p>
            ) : (
              <div className="space-y-0.5">
                {byType.map(t => (
                  <BarRow key={t.name} name={t.name} count={t.count} max={maxType} tone="violet" />
                ))}
              </div>
            )}
          </section>

          <section className="rounded-xl border border-border bg-card p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Сложность
            </h3>
            {byDiff.length === 0 ? (
              <p className="text-xs text-muted-foreground">Нет данных по сложности</p>
            ) : (
              <div className="space-y-0.5">
                {byDiff.map(t => (
                  <BarRow key={t.name} name={t.name} count={t.count} max={maxDiff} tone="amber" />
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {solved > 0 && (
        <section className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border/60 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 justify-between">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Прокси-серверы
              </h3>
              <p className="text-[11px] text-muted-foreground/80 mt-0.5">
                host:port выхода · {byProxyAll.length.toLocaleString('ru-RU')} адресов
                {byProxyAll[0] && hotProxyShare >= 25 ? (
                  <span className="text-amber-400/90">
                    {' '}· топ {byProxyAll[0].proxy} даёт {hotProxyShare}% капч
                  </span>
                ) : null}
              </p>
            </div>
            <div className="relative w-full sm:w-56">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Фильтр по IP / порту"
                className="w-full h-8 pl-8 pr-2 text-xs rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-violet-500/50"
              />
            </div>
          </div>

          {byProxy.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground">
              Ничего не найдено по «{q}»
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30">
                    <th className="px-4 py-2 font-medium">Прокси</th>
                    <th className="px-3 py-2 font-medium tabular-nums text-right">Капч</th>
                    <th className="px-3 py-2 font-medium tabular-nums text-right">Доля</th>
                    <th className="px-3 py-2 font-medium tabular-nums text-right">Ср. циклов</th>
                    <th className="px-4 py-2 font-medium">Типы</th>
                  </tr>
                </thead>
                <tbody>
                  {byProxy.map((row, i) => {
                    const share = solved ? Math.round((row.count / solved) * 100) : 0
                    return (
                      <tr
                        key={row.proxy}
                        className={`border-t border-border/50 hover:bg-muted/20 ${i === 0 && share >= 20 ? 'bg-amber-500/5' : ''}`}
                      >
                        <td className="px-4 py-2.5">
                          <div className="font-mono text-[11px] text-foreground">{row.proxy}</div>
                          {i === 0 && share >= 20 && (
                            <div className="text-[10px] text-amber-400/90 mt-0.5">Чаще остальных</div>
                          )}
                        </td>
                        <td className="px-3 py-2.5 tabular-nums text-right font-semibold text-violet-300">
                          {row.count.toLocaleString('ru-RU')}
                        </td>
                        <td className="px-3 py-2.5 tabular-nums text-right text-muted-foreground">
                          {share}%
                        </td>
                        <td className="px-3 py-2.5 tabular-nums text-right text-foreground">
                          {fmtAvg(row.cycles_sum, row.count)}
                        </td>
                        <td className="px-4 py-2.5">
                          <TypeChips types={row.types} />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  )
}

function HeroMetric({
  icon, label, value, hint,
}: {
  icon: ReactNode
  label: string
  value: string
  hint?: string
}) {
  return (
    <div className="px-4 py-3.5" title={hint}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
        <span className="opacity-70">{icon}</span>
        {label}
      </div>
      <div className="mt-1.5 text-xl font-bold tabular-nums text-foreground leading-none">{value}</div>
    </div>
  )
}
